import { AppFormSuggestionItem, AppFormSuggestionDisplayItem } from "#components";
import { faSparkles } from "@fortawesome/pro-regular-svg-icons";
import Fuse from "fuse.js";
import type { FieldDefinition } from "~/models/Automation";
import type { Form } from "~/models/Form";
import type { FormField } from "~/models/FormField";

export function makeFormFieldMap(form: Form, fields: FieldDefinition[]) {
  const fieldCollection = createFieldCollection(fields);
  const fieldMap = createFieldCollection((form.metadata?.fields ?? []).map((field) => {
    return {
      ...field,
      icon: faSparkles,
      metadata: {
        ...field.metadata,
        is_template_field: true,
      },
    };
  })).add(...fields);

  return fieldMap.items.map((field: FieldDefinition) => {
    if (field.key === "brand.website" && !field.default_value) {
      field.default_value = fieldCollection.replaceContent(
        "account.brand.website",
      );
    }
    return field;
  });
}

export async function makeAndLoadFormFieldMap(form: Form) {
  if (!form.id) return [];

  const { data } = await useApi().forms.listFieldMap(form.id, form ?? {});

  return makeFormFieldMap(form, data);
}

export function makeFormRecallItems(
  form?: Form,
  fields: FieldDefinition[] = [],
): FieldDefinition<any, FormField>[] {
  if (!form) return [];

  return fields
    .map((field: FieldDefinition) => {
      for (const form_field of form?.fields ?? []) {
        if (field.key.includes(form_field.id)) {
          return {
            ...field,
            metadata: form_field,
          };
        }
      }

      return null;
    })
    .filter((field) => !!field) as FieldDefinition<any, FormField>[];
}

export interface FormRecallMention {
  timestamp: number;
  fields: FieldDefinition[];
  match: {
    between: string[];
    name: string;
    fields: FieldDefinition[];
  };
  mention: ReturnType<typeof useMention<FieldDefinition<any, FormField>>>;
}

export function makeFormRecallMention(
  form?: Form,
  fields: FieldDefinition[] = [],
  at: FormField | undefined = undefined,
): FormRecallMention {
  const recall_items = makeFormRecallItems(form, fields);

  const items = at
    ? recall_items.filter((item: FieldDefinition<any, FormField>) => {
      return (
        item.metadata.id !== at.id &&
        item.metadata.position < at.position
      );
    })
    : recall_items;

  const keys = items.map((item) => item.key);

  return {
    timestamp: Date.now(),
    fields: fields.filter((field) => {
      return !keys.includes(field.key);
    }),
    match: {
      between: ["@", "@"],
      name: "recall-mention",
      fields: items,
    },
    mention: useMention<FieldDefinition<any, FormField>>({
      name: "recall-mention",
      class: "hd-recall-mention",
      attributes: ["label", "key"],
      matchKey: "key",
      prefix: "@",
      suffix: "@",
      component: AppFormSuggestionItem,
      suggestionComponent: AppFormSuggestionDisplayItem,
      items,
      render: (item) => {
        return item ? `@${item.key}@` : `@undefined@`;
      },
      query: ({ items, query }) => {
        if (query) {
          const fuse = new Fuse(items, {
            shouldSort: true,
            keys: ["label", "key"],
            isCaseSensitive: false,
            threshold: 0.5,
            useExtendedSearch: true,
            ignoreLocation: true
          });

          return fuse.search(query).map(({ item }) => {
            return item;
          });
        }

        return items;
      },
    }),
  };
}